import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card, CardBody, Input, Label } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getCountrySelectOptions } from '@repo/common-types';

import StandardLayout from '@/common/layouts/StandardLayout';
import DataTable from '@/common/tableData/DataTable';
import Paginator from '@/pages/common/paginator';
import { formatDateYyyyMmDd } from '@/common/utils/format-date';
import {
  fetchAgentByIdRest,
  fetchAgentDirectReferralIdsRest,
  fetchAgentDirectReferralsRest,
  fetchUnassignedPlayerIdsRest,
  fetchUnassignedPlayersRest,
} from '@/services/agents/agents-service';

import AgentAssociationModal from './components/AgentAssociationModal';

export const NO_AGENT_ID = 'no-agent';

const COUNTRY_OPTIONS = getCountrySelectOptions();

const AgentProfilePage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const isNoAgent = agentId === NO_AGENT_ID;

  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(500);
  const [search, setSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<{ value: string; label: string }[]>([]);

  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);

  const countryCodes = useMemo(
    () => (selectedCountries.length > 0 ? selectedCountries.map((c) => c.value) : undefined),
    [selectedCountries],
  );

  const { data: agent, isLoading: isLoadingAgent } = useQuery({
    queryKey: ['agentProfile', agentId],
    queryFn: () => fetchAgentByIdRest(agentId as string),
    enabled: Boolean(agentId) && !isNoAgent,
    staleTime: 30_000,
  });

  const {
    data: players,
    isLoading: isLoadingPlayers,
    error: playersError,
    refetch: refetchPlayers,
  } = useQuery({
    queryKey: isNoAgent
      ? ['unassignedPlayers', skip, take, search, countryCodes]
      : ['agentDirectReferrals', agentId, skip, take, search],
    queryFn: () =>
      isNoAgent
        ? fetchUnassignedPlayersRest({ skip, take, search: search || undefined, countryCodes })
        : fetchAgentDirectReferralsRest(agentId as string, { skip, take, search: search || undefined }),
    enabled: Boolean(agentId),
    staleTime: 15_000,
  });

  const currentPageIds = useMemo(() => players?.items.map((u) => u.id) ?? [], [players?.items]);
  const isAllCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedUserIds.has(id));

  const togglePageSelection = useCallback(() => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      const allSelected = currentPageIds.every((id) => prev.has(id));
      currentPageIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }, [currentPageIds]);

  const toggleUserSelection = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: () => <input type="checkbox" checked={isAllCurrentPageSelected} onChange={togglePageSelection} />,
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedUserIds.has(row.original.id)}
            onChange={() => toggleUserSelection(row.original.id)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
      },
      { header: 'Alias', accessorKey: 'alias', enableSorting: false },
      { header: 'Email', accessorKey: 'email', enableSorting: false },
      {
        header: 'Agent',
        accessorKey: 'isAgent',
        enableSorting: false,
        cell: ({ row }) => (row.original.isAgent ? 'Yes' : 'No'),
      },
      { header: 'Depth', accessorKey: 'referralDepth', enableSorting: false },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        enableSorting: false,
        cell: ({ row }) => formatDateYyyyMmDd(row.original.createdAt),
      },
    ],
    [isAllCurrentPageSelected, selectedUserIds, togglePageSelection, toggleUserSelection],
  );

  const handleSelectAll = useCallback(async () => {
    if (!agentId) return;
    setIsSelectAllLoading(true);
    try {
      const ids = isNoAgent
        ? await fetchUnassignedPlayerIdsRest({ search: search || undefined, countryCodes })
        : await fetchAgentDirectReferralIdsRest(agentId, { search: search || undefined });
      setSelectedUserIds(new Set(ids));
      toast.success(`Selected ${ids.length} users`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to select all');
    } finally {
      setIsSelectAllLoading(false);
    }
  }, [agentId, isNoAgent, search, countryCodes]);

  const clearSelection = useCallback(() => setSelectedUserIds(new Set()), []);

  const sourceAgent = useMemo(
    () => (isNoAgent ? null : agent ? { id: agent.id, alias: agent.alias, email: agent.email } : null),
    [isNoAgent, agent],
  );

  const handleSearch = useCallback((value: string | number) => {
    setSkip(0);
    setSearch(String(value));
  }, []);

  const handleMigrationSuccess = useCallback(async () => {
    setShowMigrationModal(false);
    setSelectedUserIds(new Set());
    await refetchPlayers();
  }, [refetchPlayers]);

  const headerExtra = (
    <div className="d-flex align-items-center gap-2">
      <span className="text-muted">Selected: {selectedUserIds.size}</span>
      <Button outline color="dark" disabled={isSelectAllLoading} onClick={handleSelectAll}>
        Select All
      </Button>
      <Button outline color="dark" disabled={selectedUserIds.size === 0} onClick={clearSelection}>
        Clear
      </Button>
      <Button color="primary" disabled={selectedUserIds.size === 0} onClick={() => setShowMigrationModal(true)}>
        {isNoAgent ? 'Assign to Agent' : 'Migrate Players'}
      </Button>
    </div>
  );

  return (
    <StandardLayout pageTitle={isNoAgent ? 'Unassigned Players' : 'Agent Profile'}>
      {!agentId && <Alert color="danger">Missing agent id</Alert>}

      <Card className="mb-3">
        <CardBody>
          {isNoAgent ? (
            <>
              <strong>No Agent</strong>
              <div className="text-muted mb-3">Players without an agent referrer</div>
              <div className="d-flex align-items-center gap-3">
                <Label className="fw-semibold mb-0">Filter by Country:</Label>
                <div style={{ minWidth: 400 }}>
                  <Select
                    isMulti
                    options={COUNTRY_OPTIONS}
                    value={selectedCountries}
                    onChange={(selected) => {
                      setSelectedCountries(selected ? [...selected] : []);
                      setSkip(0);
                      setSelectedUserIds(new Set());
                    }}
                    placeholder="Select countries..."
                    isClearable
                  />
                </div>
              </div>
            </>
          ) : isLoadingAgent ? (
            <div>Loading...</div>
          ) : !agent ? (
            <Alert color="danger">Agent not found</Alert>
          ) : (
            <>
              <strong>{agent.alias}</strong>
              <div className="text-muted">{agent.email}</div>
            </>
          )}
        </CardBody>
      </Card>

      {playersError && <Alert color="danger">Unable to load players</Alert>}
      {isLoadingPlayers && <Alert color="info">Loading...</Alert>}

      <DataTable
        title={isNoAgent ? 'Unassigned Players' : 'Direct Referrals'}
        data={players?.items ?? []}
        columns={columns}
        headerExtra={headerExtra}
        onSearch={handleSearch}
        enableSorting={false}
      />

      <Paginator
        take={take}
        skip={skip}
        showing={players?.items?.length ?? 0}
        totalCount={players?.totalCount ?? 0}
        setTake={(v) => {
          setTake(v);
          setSkip(0);
        }}
        setSkip={setSkip}
      />

      {(isNoAgent || agent) && (
        <AgentAssociationModal
          isOpen={showMigrationModal}
          onClose={() => setShowMigrationModal(false)}
          sourceAgent={sourceAgent}
          selectedUserIds={Array.from(selectedUserIds)}
          onSuccess={handleMigrationSuccess}
        />
      )}
    </StandardLayout>
  );
};

export default AgentProfilePage;
